import { useState } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faTrash, faCheck } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function GoalCard({ goal, familyId }) {
  const [isDeleting, setIsDeleting] = useState(false);

  const progressPercentage = goal.targetCount > 0 
    ? Math.min((goal.currentCount / goal.targetCount) * 100, 100)
    : 0;

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'families', familyId, 'goals', goal.id));
      toast.success('Goal deleted');
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleComplete = async () => {
    try {
      // Mark goal as completed
      await updateDoc(doc(db, 'families', familyId, 'goals', goal.id), {
        completed: true,
        completedDate: new Date()
      });

      // Create achievement
      const { collection: collectionFn, addDoc: addDocFn, serverTimestamp } = await import('firebase/firestore');
      await addDocFn(collectionFn(db, 'families', familyId, 'achievements'), {
        goalId: goal.id,
        title: goal.title,
        completedDate: serverTimestamp(),
        celebrationShown: false
      });

      toast.success('Goal completed! 🎉');
    } catch (error) {
      console.error('Error completing goal:', error);
      toast.error('Failed to complete goal');
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-border p-6 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <FontAwesomeIcon icon={faBullseye} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-text-primary">{goal.title}</h3>
            {goal.description && (
              <p className="text-sm text-text-secondary mt-1">{goal.description}</p>
            )}
          </div>
        </div>
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="p-2 text-text-secondary hover:text-red-500 transition-colors"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-text-primary">
            Progress
          </span>
          <span className="text-sm font-semibold text-primary">
            {goal.currentCount} / {goal.targetCount}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-primary to-primary-light h-3 rounded-full transition-all"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Goal Type Badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full font-semibold">
          {goal.type === 'streak' && '🔥 Streak'}
          {goal.type === 'count' && '📊 Count'}
          {goal.type === 'first-time' && '⭐ First Time'}
          {goal.type === 'custom' && '✨ Custom'}
        </span>
        {goal.experienceType && (
          <span className="text-xs px-2 py-1 bg-gray-100 text-text-secondary rounded-full">
            {goal.experienceType}
          </span>
        )}
      </div>

      {/* Complete Button */}
      {progressPercentage >= 100 && !goal.completed && (
        <button
          onClick={handleComplete}
          className="w-full px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
        >
          <FontAwesomeIcon icon={faCheck} />
          Mark as Complete
        </button>
      )}

      {/* Created Date */}
      {goal.createdAt && (
        <p className="text-xs text-text-secondary mt-4">
          Created {goal.createdAt?.toDate ? format(goal.createdAt.toDate(), 'MMM d, yyyy') : ''}
        </p>
      )}
    </div>
  );
}
